import { ClientReplyDelivery, CommentSavedStatus } from '@overlayqa/shared/ui';
import { useAuth } from '../../lib/auth';
import { commentsApi } from '../../lib/api/comments';
import { downloadCommentFile, toUserFacingMessage } from '@overlayqa/shared';
import { useState, useEffect, useRef, useCallback } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { BubbleChatIcon } from '@hugeicons/core-free-icons';
import { toast } from 'sonner';
import type { IssueComment, MentionableUser, CommentVisibility } from '@overlayqa/shared';
import {
  useIssueComments,
  useUpdateComment,
  useDeleteComment,
} from '../../hooks/useIssueComments';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { log } from '@/lib/logger';
import { capture } from '@/lib/analytics';

// Pure @mention transforms (phase C0). Storage format @[<uuid>] <-> display format @Name.
import {
  contentForInput,
  contentForStorage,
  extractMentionIds,
} from './commentMentions';
import { useCommentStream } from './useCommentStream';
import { useMentionAutocomplete } from './useMentionAutocomplete';
import { CommentItem } from './CommentItem';
import { CommentComposer } from './CommentComposer';
import { useCommentDraft } from './useCommentDraft';

interface CommentThreadProps {
  issueId: string;
  teamMembers: MentionableUser[];
}

export function CommentThread({ issueId, teamMembers }: CommentThreadProps) {
  const { getToken } = useAuth();
  const { data: currentUser } = useCurrentUser();
  // Internal DB user ID — matches the authorId field on native comments
  const userId = currentUser?.id ?? '';
  const { data: loadedComments, isLoading, error, refetch, isFetching } = useIssueComments(issueId);
  const comments = loadedComments ?? [];
  const { saved, content, setContent, visibility, setVisibility, submit: handleSubmit, isPending, attachments, setAttachments, readingFiles, setReadingFiles } = useCommentDraft(issueId, teamMembers);
  const handleVisibilityChange = useCallback((value: CommentVisibility) => {
    setVisibility(value);
    capture('comment_visibility_selected', { visibility: value, surface: 'dashboard' });
  }, [setVisibility]);
  const updateComment = useUpdateComment(issueId);
  const deleteComment = useDeleteComment(issueId);

  const editRevision = useRef(0);
  const updating = useRef(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  // Comment id whose delete is in flight. The menu sits open through the
  // round-trip, so without this the "Delete" item stayed live and a second
  // click fired a duplicate DELETE.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // @mention autocomplete for the composer (phase C2). The edit textarea is
  // deliberately NOT wired through it: the dropdown, the keydown and the caret
  // all belong to the composer's textarea, so an "@" typed while editing used
  // to open the composer's list and a pick wrote into the composer draft
  // (found 2026-09-08). Mentions typed by hand as @Name still resolve to the
  // stored @[id] form on Save through contentForStorage.
  const {
    mentionQuery,
    filteredMembers,
    activeIndex,
    handleTextChange,
    insertMention,
    handleKeyDown: handleMentionKeyDown,
  } = useMentionAutocomplete({ teamMembers, textareaRef });

  // Live updates: the ticketed SSE stream and its reconnect ladder (phase C1).
  useCommentStream(issueId);

  // Auto-scroll to bottom on new comments
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [comments.length]);

  // Close comment action menu on click outside
  useEffect(() => {
    if (!menuOpenId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpenId]);

  const handleEditSubmit = useCallback(async () => {
    const editingComment = comments.find(comment => comment.id === editingId);
    if (!editingId || !editingComment || (!editContent.trim() && !editingComment.attachments?.length) || updating.current || updateComment.isPending) return;
    updating.current = true;
    const submittedRevision = editRevision.current;
    const resolved = contentForStorage(editContent.trim(), teamMembers);
    try {
      await updateComment.mutateAsync({
        commentId: editingId,
        body: {
          content: resolved,
          mentionedUserIds: extractMentionIds(resolved),
        },
      });
      // Starting, changing or cancelling an edit advances its revision below.
      // An older acknowledgement must not close that newer editing session.
      if (editRevision.current === submittedRevision) {
        setEditingId(null);
        setEditContent('');
      }
    } catch (err) {
      log.error('[CommentThread] failed to update comment:', { err });
      const message = toUserFacingMessage(err, '');
      toast.error(message || 'Failed to update comment.');
    } finally {
      updating.current = false;
    }
  }, [editingId, editContent, comments, updateComment, teamMembers]);

  const handleDelete = useCallback(
    async (commentId: string) => {
      setDeletingId(commentId);
      try {
        await deleteComment.mutateAsync(commentId);
        setMenuOpenId(null);
      } catch (err) {
        log.error('[CommentThread] failed to delete comment:', { err });
        const message = toUserFacingMessage(err, '');
        toast.error(message || 'Failed to delete comment.');
      } finally {
        setDeletingId(null);
      }
    },
    [deleteComment]
  );

  if (isLoading) {
    return (
      <p className="text-sm text-gray-400 text-center py-4">
        Loading comments...
      </p>
    );
  }

  return (
    <section className="card-sm flex flex-col">
      <h3 className="section-label mb-3 flex items-center gap-2">
        <HugeiconsIcon icon={BubbleChatIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-400" aria-hidden="true" />
        Comments{loadedComments !== undefined ? ` (${comments.length})` : ''}
      </h3>

      {error && (
        <div role="alert" className="mb-3 text-sm text-red-700">
          <p>{comments.length ? 'Could not refresh comments. Previously loaded comments are shown.' : 'Could not load comments.'}</p>
          <button type="button" className="btn-secondary btn-sm mt-2" onClick={() => void refetch()} disabled={isFetching}>
            <span className="text-cap-trim">{isFetching ? 'Retrying...' : 'Retry'}</span>
          </button>
        </div>
      )}

      <div ref={listRef} className="flex-1 overflow-y-auto space-y-3 max-h-96 mt-4">
        {comments.length === 0 ? (error ? null : (
          <p className="text-sm text-gray-400 text-center py-4">No comments yet</p>
        )) : (
          comments.map((comment: IssueComment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onDownload={async id => {
                const token = await getToken();
                if (!token) throw new Error('Please sign in to download this file.');
                downloadCommentFile(await commentsApi.attachment(token, issueId, comment.id, id));
              }}
              fetchFile={async id => {
                const token = await getToken();
                if (!token) throw new Error('Please sign in to play this recording.');
                return commentsApi.attachment(token, issueId, comment.id, id);
              }}
              onVideoEvent={event => capture(event, { surface: 'dashboard' })}
              isOwn={comment.source === 'native' && comment.authorId === userId}
              teamMembers={teamMembers}
              menuOpen={menuOpenId === comment.id}
              menuRef={menuRef}
              onToggleMenu={() => setMenuOpenId(menuOpenId === comment.id ? null : comment.id)}
              onStartEdit={() => {
                editRevision.current += 1;
                setEditingId(comment.id);
                setEditContent(contentForInput(comment.content, teamMembers));
                setMenuOpenId(null);
              }}
              onDelete={() => handleDelete(comment.id)}
              deletingId={deletingId}
              editing={editingId === comment.id}
              editContent={editContent}
              onEditContentChange={(e) => {
                editRevision.current += 1;
                setEditContent(e.target.value);
              }}
              onSaveEdit={handleEditSubmit}
              isSaving={updateComment.isPending}
              onCancelEdit={() => {
                editRevision.current += 1;
                setEditingId(null);
                setEditContent('');
              }}
            />
          ))
        )}
      </div>

      <CommentSavedStatus saved={saved} />
      <ClientReplyDelivery issueId={issueId} visibility={visibility} api={{
        clientEmail: async id => { const token = await getToken(); if (!token) throw new Error('Sign in to check email updates.'); return commentsApi.clientEmail(token, id); },
        retryClientEmail: async (id, batchId) => { const token = await getToken(); if (!token) throw new Error('Sign in to retry the email.'); return commentsApi.retryClientEmail(token, id, batchId); },
      }} refreshKey={comments.map(c => c.id).join(',')} />
      <CommentComposer
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        readingFiles={readingFiles}
        onReadingFilesChange={setReadingFiles}
        content={content}
        setContent={setContent}
        teamMembers={teamMembers}
        textareaRef={textareaRef}
        mentionQuery={mentionQuery}
        filteredMembers={filteredMembers}
        activeIndex={activeIndex}
        insertMention={insertMention}
        handleTextChange={handleTextChange}
        handleMentionKeyDown={handleMentionKeyDown}
        onSubmit={handleSubmit}
        isPending={isPending}
        visibility={visibility}
        onVisibilityChange={handleVisibilityChange}
      />
    </section>
  );
}
